import { useState, useRef } from 'react';
import { COMPANY_INFO } from '../data/staticData';
import { submitWebform } from '../lib/cmsClient';

export const INSULATION_OPTIONS = [
  { id: '100', mm: 100, name: 'Isolering 100mm', pricePerM2: 83.50, description: 'Supplerende efterisolering af eksisterende lag' },
  { id: '150', mm: 150, name: 'Isolering 150mm', pricePerM2: 98.50, description: 'Velegnet til ældre huse med 100mm eksisterende' },
  { id: '200', mm: 200, name: 'Isolering 200mm', pricePerM2: 118.50, description: 'Populært valg til markant varmetabsreduktion' },
  { id: '250', mm: 250, name: 'Isolering 250mm', pricePerM2: 143.50, description: 'Effektiv isolering mod kuldebroer og varmetab' },
  { id: '300', mm: 300, name: 'Isolering 300mm', pricePerM2: 158.50, description: 'Opfylder moderne standarder for lavenergi' },
  { id: '350', mm: 350, name: 'Isolering 350mm', pricePerM2: 173.50, description: 'Højeffektiv isolering med optimal varmebesparelse' },
  { id: '400', mm: 400, name: 'Isolering 400mm', pricePerM2: 213.50, description: 'Maksimal varmeøkonomi og fremtidssikring' },
  { id: '450', mm: 450, name: 'Isolering 450mm', pricePerM2: 228.50, description: 'Total energirenovering for laveste varmeregning' },
];

export default function InsulationCalculator() {
  const [selectedInsulation, setSelectedInsulation] = useState('200');
  const [groundSize, setGroundSize] = useState(130);
  const [bridgeLength, setBridgeLength] = useState(10);
  const [showResult, setShowResult] = useState(false);

  const resultRef = useRef(null);
  const startedTrackingRef = useRef(false);

  // Form state
  const [formState, setFormState] = useState({
    Navn: '',
    Mail: '',
    Mobil: '',
    Adresse: '',
    Postnummer: '',
    Besked: '',
    Finansiering: false,
    AccepterBetingelser: false,
    website_url: '' // Honeypot
  });

  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  // Beregning af estimeret pris baseret på Excel formel:
  // Loftets grundplan * Enhedspris + (hvis gangbro > 5m: (gangbro - 5) * 270 kr.)
  const currentOption = INSULATION_OPTIONS.find(opt => opt.id === selectedInsulation) || INSULATION_OPTIONS[2];
  const insulationCost = groundSize * currentOption.pricePerM2;
  const extraBridgeMeters = Math.max(0, bridgeLength - 5);
  const bridgeCost = extraBridgeMeters * 270;
  const calculatedTotal = insulationCost + bridgeCost;

  const handleCalculateClick = () => {
    setShowResult(true);
    setTimeout(() => {
      if (resultRef.current) {
        resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    setFileError('');
    const selectedFiles = Array.from(e.target.files || []);
    if (!selectedFiles.length) return;

    const totalAllowed = 5;
    const combinedFiles = [...files, ...selectedFiles].slice(0, totalAllowed);

    if (files.length + selectedFiles.length > totalAllowed) {
      setFileError(`Du kan maksimalt vedhæfte op til ${totalAllowed} billeder i alt.`);
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf'];
    for (const f of combinedFiles) {
      if (f.size > 10 * 1024 * 1024) {
        setFileError(`Billedet "${f.name}" er større end tilladt (maks 10 MB per fil).`);
        return;
      }
      if (!allowedTypes.includes(f.type) && !/\.(jpe?g|png|webp|gif|pdf)$/i.test(f.name)) {
        setFileError(`Filen "${f.name}" har et ugyldigt format. Tilladte formater er JPG, PNG, WEBP, GIF og PDF.`);
        return;
      }
    }

    const totalSize = combinedFiles.reduce((acc, f) => acc + f.size, 0);
    if (totalSize > 20 * 1024 * 1024) {
      setFileError('De samlede filer overskrider grænsen på 20 MB.');
      return;
    }

    setFiles(combinedFiles);
    e.target.value = '';
  };

  const removeFile = (indexToRemove) => {
    setFiles(prev => prev.filter((_, idx) => idx !== indexToRemove));
    setFileError('');
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formState.AccepterBetingelser) {
      alert('Du skal acceptere vilkårene for brug af oplysninger og privatlivspolitik for at sende.');
      return;
    }

    setSubmitting(true);
    setSubmitStatus(null);

    const beregningsTekst = `[PRISBEREGNER: ISOLERING AF LOFT]
1. Mængde isolering: ${currentOption.name} (${currentOption.mm} mm) - ${currentOption.pricePerM2.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr./m²
2. Husets grundplan: ${groundSize} m²
3. Gangbro længde: ${bridgeLength} m (${bridgeLength <= 5 ? '5 m inkluderet' : `${extraBridgeMeters} m tillæg á 270 kr.`})
Beregnet pris: ${calculatedTotal.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr. inkl. moms
Finansiering på plads: ${formState.Finansiering ? 'Ja' : 'Nej/Ikke oplyst'}
Vedhæftede filer: ${files.length > 0 ? `${files.length} stk.` : 'Ingen'}
Evt. besked: ${formState.Besked || 'Ingen'}`;

    let res;
    if (files.length > 0) {
      const formData = new FormData();
      formData.append('Navn', formState.Navn);
      formData.append('Mail', formState.Mail);
      formData.append('Mobil', formState.Mobil);
      formData.append('Adresse', formState.Adresse);
      formData.append('Postnummer', formState.Postnummer);
      formData.append('Besked', beregningsTekst);
      formData.append('Finansiering', formState.Finansiering ? 'Ja' : 'Nej');
      formData.append('website_url', formState.website_url); // Honeypot

      files.forEach((file) => {
        formData.append('billeder', file);
      });

      res = await submitWebform(formData);
    } else {
      const payload = {
        Navn: formState.Navn,
        Mail: formState.Mail,
        Mobil: formState.Mobil,
        Adresse: formState.Adresse,
        Postnummer: formState.Postnummer,
        Besked: beregningsTekst,
        Finansiering: formState.Finansiering,
        website_url: formState.website_url // Honeypot
      };
      res = await submitWebform(payload);
    }

    setSubmitting(false);

    if (res.ok) {
      setSubmitStatus('success');
    } else {
      setSubmitStatus('error');
    }
  };

  return (
    <div className="space-y-12">
      
      {/* Selve Beregneren */}
      <div className="bg-white border border-neutral-200 shadow-md p-6 sm:p-10 lg:p-12 space-y-12">
        
        {/* SEKTION 1: Mængde isolering */}
        <div className="space-y-6">
          <div className="flex items-center gap-3.5 border-b border-neutral-200 pb-4">
            <span className="w-9 h-9 rounded-full bg-[#BE0F12] text-white flex items-center justify-center font-['Saira'] font-bold text-lg shadow-sm shrink-0">
              1
            </span>
            <h3 className="font-['Saira'] font-bold text-xl sm:text-2xl text-stone-900 m-0">
              Mængde isolering
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {INSULATION_OPTIONS.map((opt) => {
              const isSelected = selectedInsulation === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setSelectedInsulation(opt.id)}
                  className={`group relative text-left p-3.5 sm:p-4 rounded-none border transition-all duration-200 flex flex-col justify-between cursor-pointer ${
                    isSelected
                      ? 'border-2 border-[#42b732] ring-2 ring-[#42b732]/25 bg-stone-50/70 shadow-md'
                      : 'border-neutral-200 bg-white hover:border-[#42b732]/60 hover:bg-neutral-50/50'
                  }`}
                >
                  {/* Billede af isolering */}
                  <div className="relative w-full h-32 sm:h-36 bg-white overflow-hidden mb-3 p-2 flex items-center justify-center border border-neutral-100">
                    <img
                      src="https://assets.auxodev.dk/assets/78966fd0-599e-4938-a2ab-b403c14ab21e/isolering2.jpg"
                      alt={opt.name}
                      className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
                      loading="lazy"
                    />
                    {isSelected && (
                      <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#42b732] text-white flex items-center justify-center text-xs shadow-md ring-2 ring-white">
                        <i className="fa-solid fa-check"></i>
                      </div>
                    )}
                  </div>

                  {/* Titel & Checkbox */}
                  <div className="flex items-center justify-between gap-2">
                    <h4 className="font-['Saira'] font-bold text-base sm:text-lg text-stone-900 m-0">
                      {opt.name}
                    </h4>
                    <div className={`w-5 h-5 rounded-none border flex items-center justify-center shrink-0 transition-colors ${
                      isSelected ? 'bg-[#42b732] border-[#42b732] text-white' : 'border-neutral-300 bg-white'
                    }`}>
                      {isSelected && <i className="fa-solid fa-check text-[10px]"></i>}
                    </div>
                  </div>

                  <p className="text-xs text-stone-500 mt-1 font-['Open_Sans'] leading-normal">
                    {opt.description}
                  </p>
                </button>
              );
            })}
          </div>
        </div>

        {/* SEKTION 2: Angiv størrelsen på husets grundplan */}
        <div className="space-y-6">
          <div className="flex items-center gap-3.5 border-b border-neutral-200 pb-4">
            <span className="w-9 h-9 rounded-full bg-[#BE0F12] text-white flex items-center justify-center font-['Saira'] font-bold text-lg shadow-sm shrink-0">
              2
            </span>
            <h3 className="font-['Saira'] font-bold text-xl sm:text-2xl text-stone-900 m-0">
              Angiv størrelsen på husets grundplan
            </h3>
          </div>

          <div className="bg-stone-50 p-6 sm:p-8 border border-neutral-200 max-w-3xl space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <label htmlFor="groundSizeInput" className="font-['Open_Sans'] font-semibold text-stone-800 text-sm sm:text-base">
                Husets grundplan (boligareal under taget):
              </label>
              <div className="flex items-center gap-2">
                <input
                  id="groundSizeInput"
                  type="number"
                  min="50"
                  max="450"
                  step="5"
                  value={groundSize}
                  onChange={(e) => setGroundSize(Math.max(50, Math.min(450, Number(e.target.value) || 50)))}
                  className="w-28 px-3 py-2 bg-white border border-neutral-300 text-stone-900 font-bold text-right text-xl focus:outline-none focus:ring-2 focus:ring-[#42b732]"
                />
                <span className="font-['Saira'] font-bold text-stone-900 text-lg">m²</span>
              </div>
            </div>

            <div className="space-y-2">
              <input
                type="range"
                min="50"
                max="450"
                step="5"
                value={groundSize}
                onChange={(e) => setGroundSize(Number(e.target.value))}
                className="w-full h-3 bg-neutral-200 rounded-none cursor-pointer accent-[#42b732]"
              />
              <div className="flex justify-between text-xs text-stone-500 font-['Open_Sans']">
                <span>50 m²</span>
                <span>150 m² (typisk parcelhus)</span>
                <span>300 m²</span>
                <span>450 m²</span>
              </div>
            </div>

            <div className="text-center font-['Saira'] font-bold text-2xl text-[#42b732]">
              {groundSize} m²
            </div>
          </div>
        </div>

        {/* SEKTION 3: Angiv længden på gangbroen */}
        <div className="space-y-6">
          <div className="flex items-center gap-3.5 border-b border-neutral-200 pb-4">
            <span className="w-9 h-9 rounded-full bg-[#BE0F12] text-white flex items-center justify-center font-['Saira'] font-bold text-lg shadow-sm shrink-0">
              3
            </span>
            <h3 className="font-['Saira'] font-bold text-xl sm:text-2xl text-stone-900 m-0">
              Angiv længden på gangbroen
            </h3>
          </div>

          <div className="bg-stone-50 p-6 sm:p-8 border border-neutral-200 max-w-3xl space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <label htmlFor="bridgeLengthInput" className="font-['Open_Sans'] font-semibold text-stone-800 text-sm sm:text-base block">
                  Længde på gangbro:
                </label>
                <span className="text-xs text-stone-500">
                  De første 5 meter gangbro er inkluderet gratis. Derefter 270 kr./m.
                </span>
              </div>
              <div className="flex items-center gap-2">
                <input
                  id="bridgeLengthInput"
                  type="number"
                  min="0"
                  max="80"
                  step="1"
                  value={bridgeLength}
                  onChange={(e) => setBridgeLength(Math.max(0, Math.min(80, Number(e.target.value) || 0)))}
                  className="w-24 px-3 py-2 bg-white border border-neutral-300 text-stone-900 font-bold text-right text-xl focus:outline-none focus:ring-2 focus:ring-[#42b732]"
                />
                <span className="font-['Saira'] font-bold text-stone-900 text-lg">m</span>
              </div>
            </div>

            <div className="space-y-2">
              <input
                type="range"
                min="0"
                max="80"
                step="1"
                value={bridgeLength}
                onChange={(e) => setBridgeLength(Number(e.target.value))}
                className="w-full h-3 bg-neutral-200 rounded-none cursor-pointer accent-[#42b732]"
              />
              <div className="flex justify-between text-xs text-stone-500 font-['Open_Sans']">
                <span>0 m (ingen gangbro)</span>
                <span>10 m (standard)</span>
                <span>40 m</span>
                <span>80 m</span>
              </div>
            </div>

            <div className="text-center font-['Saira'] font-bold text-2xl text-[#42b732]">
              {bridgeLength} m
            </div>
          </div>
        </div>

        {/* CTA BEREGN KNAP */}
        <div className="pt-4 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-neutral-200">
          <div className="text-sm text-stone-600 font-['Open_Sans']">
            Klik herunder for at beregne din uforpligtende pris med det samme.
          </div>
          <button
            type="button"
            onClick={handleCalculateClick}
            className="w-full sm:w-auto px-8 py-4 bg-[#BE0F12] hover:bg-[#A30D0F] text-white font-['Saira'] font-bold text-lg uppercase tracking-wider transition-all shadow-md hover:shadow-lg cursor-pointer flex items-center justify-center gap-3"
          >
            <i className="fa-solid fa-calculator"></i>
            <span>Få din uforpligtende pris</span>
          </button>
        </div>

      </div>

      {/* RESULTAT OG KONTAKTFORMULAR */}
      {showResult && (
        <div ref={resultRef} className="bg-white border-2 border-[#42b732] shadow-xl p-6 sm:p-10 lg:p-12 space-y-10 animate-fadeIn">
          
          {/* Prisbanner */}
          <div className="bg-[#42b732]/10 border border-[#42b732]/30 p-6 sm:p-8 text-center sm:text-left flex flex-col sm:flex-row items-center justify-between gap-6">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-[#42b732] block mb-1">
                Beregnet prisoverslag
              </span>
              <h3 className="font-['Saira'] font-bold text-2xl sm:text-3xl text-stone-900 m-0">
                Den beregnede pris er:
              </h3>
              <p className="text-xs sm:text-sm text-stone-600 mt-1 font-['Open_Sans']">
                Vejledende pris inkl. moms, materialer og montering.
              </p>
            </div>
            <div className="text-center sm:text-right">
              <div className="font-['Saira'] font-black text-3xl sm:text-5xl text-[#42b732]">
                {calculatedTotal.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr.
              </div>
              <span className="text-xs text-stone-500 font-semibold uppercase tracking-wider block mt-1">
                inkl. moms
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            
            {/* Venstre kolonne: Opsummering & Forklaring */}
            <div className="lg:col-span-5 space-y-6">
              <div>
                <h3 className="font-['Saira'] font-bold text-2xl text-stone-900 leading-snug">
                  Udfyld kontaktformularen og få et uforpligtende tilbud
                </h3>
                <p className="text-stone-600 text-sm font-['Open_Sans'] leading-relaxed mt-2">
                  Prisen er en vejledende pris inkl. moms. Der kan være en justering i tilbuddet, når vi har set dit nuværende loftrum. Udfyld kontaktformularen, så vender vi tilbage med et gratis, uforpligtende tilbud.
                </p>
              </div>

              {/* Dine valg */}
              <div className="bg-stone-50 border border-neutral-200 p-5 space-y-3">
                <h4 className="font-['Saira'] font-bold text-base text-stone-900 uppercase tracking-wide m-0">
                  Dine valg:
                </h4>
                <ul className="space-y-2 text-sm text-stone-700 font-['Open_Sans']">
                  <li className="flex items-center justify-between border-b border-stone-200 pb-1.5">
                    <span className="text-stone-500">Mængde isolering:</span>
                    <strong className="text-stone-900">{currentOption.name}</strong>
                  </li>
                  <li className="flex items-center justify-between border-b border-stone-200 pb-1.5">
                    <span className="text-stone-500">Grundplan:</span>
                    <strong className="text-stone-900">{groundSize} m²</strong>
                  </li>
                  <li className="flex items-center justify-between border-b border-stone-200 pb-1.5">
                    <span className="text-stone-500">Gangbro længde:</span>
                    <strong className="text-stone-900">{bridgeLength} m {bridgeLength <= 5 ? '(5 m inkluderet)' : `(${extraBridgeMeters} m tillæg)`}</strong>
                  </li>
                  <li className="flex items-center justify-between border-b border-stone-200 pb-1.5 pt-1 text-xs">
                    <span className="text-stone-500">Isolering ({groundSize} m² á {currentOption.pricePerM2.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr.):</span>
                    <strong className="text-stone-800">{insulationCost.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr.</strong>
                  </li>
                  {bridgeCost > 0 && (
                    <li className="flex items-center justify-between border-b border-stone-200 pb-1.5 text-xs">
                      <span className="text-stone-500">Gangbrotillæg ({extraBridgeMeters} m á 270 kr.):</span>
                      <strong className="text-stone-800">{bridgeCost.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr.</strong>
                    </li>
                  )}
                </ul>
              </div>

              {/* Direkte kontakt boks */}
              <div className="border border-neutral-200 p-5 space-y-3">
                <h4 className="font-['Saira'] font-bold text-base text-stone-900 uppercase tracking-wide m-0">
                  Kontakt os med spørgsmål:
                </h4>
                <div className="space-y-2 text-sm font-['Open_Sans']">
                  <div className="flex items-center gap-2">
                    <strong className="text-stone-900">Ring:</strong>
                    <a href={COMPANY_INFO.phoneTel} className="text-[#BE0F12] hover:underline font-bold">
                      {COMPANY_INFO.phoneFormatted}
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <strong className="text-stone-900">Skriv:</strong>
                    <a href={COMPANY_INFO.emailMailto} className="text-[#BE0F12] hover:underline">
                      {COMPANY_INFO.email}
                    </a>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-amber-50 border border-amber-200 text-amber-900 text-xs leading-relaxed space-y-1 font-['Open_Sans']">
                <div className="font-bold flex items-center gap-1.5">
                  <i className="fa-solid fa-lightbulb text-amber-700"></i>
                  Husk eftersyn af ventilation og gangbro:
                </div>
                <p>
                  Ved efterisolering af loft er det afgørende, at tagrummet fortsat er tilstrækkeligt ventileret mod fugt og skimmel. Vi gennemgår altid dit loft og sikrer korrekt udførelse.
                </p>
              </div>

            </div>

            {/* Højre kolonne: Kontaktformular */}
            <div className="lg:col-span-7">
              <div className="bg-stone-50 border border-neutral-200 p-6 sm:p-8 space-y-6">
                
                <h3 className="font-['Saira'] font-bold text-xl text-stone-900 border-b border-neutral-200 pb-3">
                  Bestil gratis tilbud ud fra din beregning
                </h3>

                {submitStatus === 'success' && (
                  <div className="p-6 bg-emerald-50 border-2 border-emerald-500 text-emerald-900 space-y-2 animate-fadeIn">
                    <div className="flex items-center gap-2 font-['Saira'] font-bold text-lg">
                      <i className="fa-solid fa-circle-check text-emerald-600 text-xl"></i>
                      <span>Tak for din henvendelse!</span>
                    </div>
                    <p className="text-sm font-['Open_Sans']">
                      Vi har modtaget din forespørgsel på loftisolering. Vi gennemgår dine specifikationer og vender tilbage med et uforpligtende tilbud hurtigst muligt.
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-300 text-red-800 text-sm">
                    Der opstod en fejl ved afsendelse. Venligst kontakt os direkte på{' '}
                    <a href={COMPANY_INFO.phoneTel} className="font-bold underline">{COMPANY_INFO.phoneFormatted}</a>.
                  </div>
                )}

                {submitStatus !== 'success' && (
                  <form onSubmit={handleFormSubmit} className="space-y-4">
                    
                    {/* Skjult honeypot til antispam */}
                    <div className="sr-only absolute -left-[9999px] -top-[9999px]" aria-hidden="true">
                      <label htmlFor="website_url_iso">Website</label>
                      <input
                        type="text"
                        name="website_url"
                        id="website_url_iso"
                        value={formState.website_url}
                        onChange={handleInputChange}
                        tabIndex={-1}
                        autoComplete="off"
                      />
                    </div>

                    {/* Resultat af beregning (read-only textarea til visning) */}
                    <div>
                      <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wide mb-1 font-['Open_Sans']">
                        Resultat af beregning
                      </label>
                      <textarea
                        readOnly
                        rows={3}
                        value={`${currentOption.name} | ${groundSize} m² grundplan | ${bridgeLength} m gangbro | Beregnet pris: ${calculatedTotal.toLocaleString('da-DK')} kr. inkl. moms`}
                        className="w-full px-3 py-2 bg-neutral-100 border border-neutral-300 text-xs font-mono text-stone-800 rounded-none focus:outline-none resize-none cursor-default"
                      />
                    </div>

                    {/* Navn & Mail */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="iso_navn" className="block text-xs font-semibold text-stone-700 uppercase tracking-wide mb-1 font-['Open_Sans']">
                          Navn*
                        </label>
                        <input
                          id="iso_navn"
                          type="text"
                          name="Navn"
                          required
                          value={formState.Navn}
                          onChange={handleInputChange}
                          placeholder="Dit fulde navn"
                          className="w-full px-3.5 py-2.5 bg-white border border-neutral-300 rounded-none text-sm text-stone-900 focus:outline-none focus:ring-2 focus:ring-[#BE0F12]"
                        />
                      </div>
                      <div>
                        <label htmlFor="iso_mail" className="block text-xs font-semibold text-stone-700 uppercase tracking-wide mb-1 font-['Open_Sans']">
                          Mail*
                        </label>
                        <input
                          id="iso_mail"
                          type="email"
                          name="Mail"
                          required
                          value={formState.Mail}
                          onChange={handleInputChange}
                          placeholder="din@mail.dk"
                          className="w-full px-3.5 py-2.5 bg-white border border-neutral-300 rounded-none text-sm text-stone-900 focus:outline-none focus:ring-2 focus:ring-[#BE0F12]"
                        />
                      </div>
                    </div>

                    {/* Mobil & Postnummer */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="iso_mobil" className="block text-xs font-semibold text-stone-700 uppercase tracking-wide mb-1 font-['Open_Sans']">
                          Mobil*
                        </label>
                        <input
                          id="iso_mobil"
                          type="tel"
                          name="Mobil"
                          required
                          value={formState.Mobil}
                          onChange={handleInputChange}
                          placeholder="Telefonnummer"
                          className="w-full px-3.5 py-2.5 bg-white border border-neutral-300 rounded-none text-sm text-stone-900 focus:outline-none focus:ring-2 focus:ring-[#BE0F12]"
                        />
                      </div>
                      <div>
                        <label htmlFor="iso_postnummer" className="block text-xs font-semibold text-stone-700 uppercase tracking-wide mb-1 font-['Open_Sans']">
                          Postnummer*
                        </label>
                        <input
                          id="iso_postnummer"
                          type="text"
                          name="Postnummer"
                          required
                          value={formState.Postnummer}
                          onChange={handleInputChange}
                          placeholder="F.eks. 8000"
                          className="w-full px-3.5 py-2.5 bg-white border border-neutral-300 rounded-none text-sm text-stone-900 focus:outline-none focus:ring-2 focus:ring-[#BE0F12]"
                        />
                      </div>
                    </div>

                    {/* Adresse */}
                    <div>
                      <label htmlFor="iso_adresse" className="block text-xs font-semibold text-stone-700 uppercase tracking-wide mb-1 font-['Open_Sans']">
                        Adresse*
                      </label>
                      <input
                        id="iso_adresse"
                        type="text"
                        name="Adresse"
                        required
                        value={formState.Adresse}
                        onChange={handleInputChange}
                        placeholder="Vejnavn og husnummer"
                        className="w-full px-3.5 py-2.5 bg-white border border-neutral-300 rounded-none text-sm text-stone-900 focus:outline-none focus:ring-2 focus:ring-[#BE0F12]"
                      />
                    </div>

                    {/* Evt. besked */}
                    <div>
                      <label htmlFor="iso_besked" className="block text-xs font-semibold text-stone-700 uppercase tracking-wide mb-1 font-['Open_Sans']">
                        Evt. besked
                      </label>
                      <textarea
                        id="iso_besked"
                        name="Besked"
                        rows={3}
                        value={formState.Besked}
                        onChange={handleInputChange}
                        placeholder="Skriv evt. bemærkninger om huset, adgangsforhold eller særlige ønsker..."
                        className="w-full px-3.5 py-2.5 bg-white border border-neutral-300 rounded-none text-sm text-stone-900 focus:outline-none focus:ring-2 focus:ring-[#BE0F12]"
                      />
                    </div>

                    {/* Billeder / Fil-upload */}
                    <div>
                      <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wide mb-1 font-['Open_Sans']">
                        Vedhæft evt. 1-5 billeder af dit hus eller loftrum
                      </label>
                      <div className="border-2 border-dashed border-neutral-300 p-4 text-center hover:bg-neutral-50 transition-colors">
                        <input
                          type="file"
                          multiple
                          accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
                          onChange={handleFileChange}
                          disabled={files.length >= 5}
                          className="block w-full text-xs text-stone-600 file:mr-4 file:py-2 file:px-4 file:rounded-none file:border-0 file:text-xs file:font-semibold file:bg-stone-800 file:text-white hover:file:bg-black cursor-pointer"
                        />
                        <p className="text-[11px] text-stone-500 mt-1">
                          Max 5 filer i alt (JPG, PNG, WEBP, PDF - maks 10 MB per fil).
                        </p>
                      </div>

                      {fileError && (
                        <p className="text-xs text-red-600 mt-1.5 font-medium">{fileError}</p>
                      )}

                      {files.length > 0 && (
                        <div className="mt-2.5 space-y-1">
                          <p className="text-xs font-bold text-stone-800">Vedhæftede filer ({files.length}/5):</p>
                          <div className="space-y-1">
                            {files.map((file, idx) => (
                              <div key={idx} className="flex items-center justify-between text-xs bg-white border border-neutral-200 px-3 py-1.5">
                                <span className="truncate max-w-[240px] text-stone-800">
                                  <i className="fa-solid fa-paperclip mr-1.5 text-stone-500"></i>
                                  {file.name}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => removeFile(idx)}
                                  className="text-red-600 hover:text-red-800 font-bold ml-2 cursor-pointer"
                                  title="Fjern fil"
                                >
                                  <i className="fa-solid fa-xmark"></i>
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Finansiering */}
                    <div className="pt-2">
                      <label className="flex items-start gap-2.5 cursor-pointer">
                        <input
                          type="checkbox"
                          name="Finansiering"
                          checked={formState.Finansiering}
                          onChange={handleInputChange}
                          className="mt-1 h-4 w-4 rounded-none border-neutral-300 text-[#BE0F12] focus:ring-[#BE0F12]"
                        />
                        <span className="text-xs sm:text-sm text-stone-700 font-['Open_Sans']">
                          Har du finansieringen på plads og ønsker et tilbud?
                        </span>
                      </label>
                    </div>

                    {/* Accepter betingelser */}
                    <div>
                      <label className="flex items-start gap-2.5 cursor-pointer">
                        <input
                          type="checkbox"
                          name="AccepterBetingelser"
                          required
                          checked={formState.AccepterBetingelser}
                          onChange={handleInputChange}
                          className="mt-1 h-4 w-4 rounded-none border-neutral-300 text-[#BE0F12] focus:ring-[#BE0F12]"
                        />
                        <span className="text-xs sm:text-sm text-stone-700 font-['Open_Sans']">
                          Jeg har læst og accepteret vilkårene for brug af oplysninger og privatlivspolitik.*
                        </span>
                      </label>
                    </div>

                    {/* Submit knap */}
                    <div className="pt-3">
                      <button
                        type="submit"
                        disabled={submitting}
                        className="w-full py-4 bg-[#BE0F12] hover:bg-[#A30D0F] text-white font-['Saira'] font-bold text-base sm:text-lg uppercase tracking-wider transition-colors shadow-md disabled:opacity-50 cursor-pointer flex items-center justify-center gap-2"
                      >
                        {submitting ? (
                          <>
                            <i className="fa-solid fa-circle-notch fa-spin"></i>
                            <span>Sender tilbudsanmodning...</span>
                          </>
                        ) : (
                          <>
                            <span>Bestil uforpligtende tilbud</span>
                            <i className="fa-solid fa-paper-plane text-sm"></i>
                          </>
                        )}
                      </button>
                    </div>

                  </form>
                )}

              </div>
            </div>

          </div>

        </div>
      )}

    </div>
  );
}
